import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type RefObject } from 'react';

export interface UseHold2xOptions {
  thresholdMs: number;
  moveCancelPx: number;
}

export interface UseHold2xResult {
  active: boolean;
  handlers: {
    onPointerDown: (e: PointerEvent) => void;
    onPointerMove: (e: PointerEvent) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
  };
}

/**
 * Long-press detector that sets playbackRate=2 while held. Cancels on
 * pointer movement past threshold or if the video is paused. Restores
 * playbackRate=1 on release.
 *
 * playbackRate changes do NOT fire play/pause/seeked, so lobby peers are
 * not yanked — by design, hold-2x is a local nudge.
 */
export function useHold2x(
  videoRef: RefObject<HTMLVideoElement>,
  options: UseHold2xOptions,
): UseHold2xResult {
  const [active, setActive] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const armedRef = useRef(false);

  const clearTimer = useCallback((): void => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const release = useCallback((): void => {
    clearTimer();
    armedRef.current = false;
    startPosRef.current = null;
    const v = videoRef.current;
    if (v && v.playbackRate !== 1) v.playbackRate = 1;
    setActive(false);
  }, [clearTimer, videoRef]);

  const onPointerDown = useCallback((e: PointerEvent): void => {
    const v = videoRef.current;
    if (!v || v.paused) return;
    armedRef.current = true;
    startPosRef.current = { x: e.clientX, y: e.clientY };
    clearTimer();
    timerRef.current = setTimeout(() => {
      if (!armedRef.current) return;
      const vv = videoRef.current;
      if (!vv || vv.paused) return;
      vv.playbackRate = 2;
      setActive(true);
    }, options.thresholdMs);
  }, [videoRef, options.thresholdMs, clearTimer]);

  const onPointerMove = useCallback((e: PointerEvent): void => {
    if (!armedRef.current || !startPosRef.current) return;
    const dx = e.clientX - startPosRef.current.x;
    const dy = e.clientY - startPosRef.current.y;
    if (Math.hypot(dx, dy) > options.moveCancelPx) release();
  }, [options.moveCancelPx, release]);

  const onPointerUp = useCallback((): void => { release(); }, [release]);
  const onPointerCancel = useCallback((): void => { release(); }, [release]);

  useEffect(() => () => { clearTimer(); }, [clearTimer]);

  const handlers = useMemo(
    () => ({ onPointerDown, onPointerMove, onPointerUp, onPointerCancel }),
    [onPointerDown, onPointerMove, onPointerUp, onPointerCancel],
  );

  return { active, handlers };
}
